/* Per-route <head> metadata.
 *
 * applyMeta() runs on every navigation in the browser and inside puppeteer
 * at prerender time, so the static snapshot and the hydrated page agree.
 * The site-wide Organization and WebSite JSON-LD live in index.html.
 */

import { PRODUCTS, INDUSTRIES, FOUNDER } from './data.js';
import { routePath } from './routes.js';

const SITE_URL = 'https://evonexon.com';
const OG_IMAGE = `${SITE_URL}/assets/og-image.png`;

function metaFor(route) {
  if (route.page === 'product') {
    const p = PRODUCTS.find(x => x.key === route.key);
    return { title: `${p.name}: ${p.tagline.replace(/\.$/, '')} | EvoNexon`, description: p.desc };
  }
  if (route.page === 'industry') {
    const d = INDUSTRIES.find(x => x.key === route.key);
    return { title: `AI Visual Inspection for ${d.name} | EvoNexon`, description: `${d.intro} VIAI catches ${d.teaser.toLowerCase()}` };
  }
  if (route.page === 'about') {
    return {
      title: 'About EvoNexon: Canadian Industrial AI',
      description: 'EvoNexon is a Toronto company building VIAI, a visual inspection system a plant manager can stand up themselves, without a six-month integration or a data-science team.',
    };
  }
  return {
    title: 'EvoNexon: AI Visual Inspection for Manufacturing',
    description: 'VIAI checks every unit on your line in under 50 ms. Train it on 30 photos of good parts. Runs fully offline, live by end of shift.',
  };
}

function jsonLdFor(route) {
  if (route.page === 'product') {
    const p = PRODUCTS.find(x => x.key === route.key);
    return [{
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: p.name,
      description: p.desc,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      publisher: { '@type': 'Organization', name: 'EvoNexon' },
      url: SITE_URL + routePath(route),
    }];
  }
  if (route.page === 'about') {
    return [{
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Alireza Ghasemieh',
      jobTitle: 'Founder & CEO',
      worksFor: { '@type': 'Organization', name: 'EvoNexon' },
      sameAs: [FOUNDER.linkedin, FOUNDER.scholar],
    }];
  }
  return [];
}

function setMeta(key, value, attr = 'name') {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

export function applyMeta(route) {
  const { title, description } = metaFor(route);
  const url = SITE_URL + routePath(route);
  document.title = title;
  setMeta('description', description);
  setMeta('og:title', title, 'property');
  setMeta('og:description', description, 'property');
  setMeta('og:url', url, 'property');
  setMeta('og:image', OG_IMAGE, 'property');
  setMeta('twitter:title', title);
  setMeta('twitter:description', description);

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', url);

  document.head.querySelectorAll('script[data-seo="route"]').forEach(el => el.remove());
  jsonLdFor(route).forEach((payload) => {
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.dataset.seo = 'route';
    el.textContent = JSON.stringify(payload);
    document.head.appendChild(el);
  });
}
